/**
 * @deprecated Temporary compatibility shim added during the v4.1.5 -> v16.x SDK upgrade.
 *
 * Re-derives the pre-Turns shape (`messages`, `streamingMessage: string | null`,
 * `isConnectingToThread`, plus `createThreadAndStart` / `continueThread` / `reconnectToThread`)
 * from the v16 *public* curated hooks so existing aikit-web consumers
 * (`ChatPanel`, `MessagesList`, `useConstraintsMetadata`, `FinecoPortfolioCompare`)
 * keep compiling unchanged — without depending on internal context APIs.
 *
 * Source of truth: Section 4 of
 * `docs/superpowers/notes/2026-05-05-sdk-v16-migration-map.md` (with the Task 1.2
 * correction noting that `useStreetBeatChatContext` is not reachable through the
 * v16 package's `exports` map; the shim sources the same fields from
 * `useStreetBeatChat`, `useChatState`, and `useConversationFlow` instead).
 *
 * Tracking issue: <fill in once issue is created — see the repo's issue tracker>
 * Removal milestone: native-turns adoption project (separate from this upgrade).
 *
 * Do not extend this shim. New code should consume `useStreetBeatChat()` (turns)
 * and the dedicated v16 curated hooks (`useChatState`, `useConversationFlow`,
 * `useFiles`, `useThreadMetadata`, `useChatErrors`, ...) directly.
 */

import { useMemo } from 'react';
import {
  useStreetBeatChat,
  useChatState,
  useConversationFlow,
} from '@dev-streetbeat/chat/react';
import type { Message, Turn, TurnContent } from '@dev-streetbeat/chat/types';

/**
 * Mirrors v16's Turn-based "internal message filtering" against the raw `messages`
 * array surfaced by `useChatState`. v6.0.0 introduced `thinking`, `tool_use`, and
 * `tool_result` content block types that produce extra assistant `Message` entries
 * which used to be invisible under v4 (collapsed into a single streaming bubble).
 * aikit-web's `ChatAgentMessage` only renders bubbles whose first content block is
 * `'text'`, so unfiltered messages render as empty bubbles (header + feedback stars
 * with no content). We hide assistant messages that have nothing displayable —
 * matching v4 emission semantics.
 *
 * A message is "displayable" if it has any of:
 * - artifacts (rendered by `MessageArtifact`)
 * - a `text` content block with non-empty `text.value`
 * - a `document`, `file`, or `plan` content block
 *
 * Messages with only `thinking` / `tool_use` / `tool_result` content (and no
 * artifacts) are filtered out.
 */
function isDisplayableAssistantMessage(message: Message): boolean {
  if (message.artifacts && message.artifacts.length > 0) return true;
  const content = message.content ?? [];
  for (const block of content) {
    if (block.type === 'text') {
      const value = block.text?.value;
      if (typeof value === 'string' && value.length > 0) return true;
    } else if (block.type === 'document' || block.type === 'file' || block.type === 'plan') {
      return true;
    }
  }
  return false;
}


/**
 * The text of the reply being streamed: the text blocks of the trailing
 * assistant message — the same message `messages` hides while it streams, so
 * the bubble shows exactly what that message will show once completed.
 *
 * Not the v16 turn's text: the SDK groups messages into a turn by `run_id`,
 * and gateway v2 gives the advisor's own message the run's id, so the turn's
 * text was "<advisor message><reply>" until the reply completed.
 */
function pickStreamingText(rawMessages: readonly Message[], isStreaming: boolean): string | null {
  if (!isStreaming) return null;
  const tail = rawMessages[rawMessages.length - 1];
  if (!tail || tail.role !== 'assistant') return null;
  let text = '';
  for (const block of tail.content ?? []) {
    if (block.type === 'text' && typeof block.text?.value === 'string') text += block.text.value;
  }
  return text;
}

/**
 * Returns true when an assistant turn is currently streaming text. Used to
 * suppress the in-progress assistant message from `messages` so we don't
 * double-render — the dedicated `streamingMessage` bubble already shows the
 * progressively growing text. Without this gate, every `content_block.delta`
 * grows the chatState message AND the streamingMessage in lockstep, and the
 * resulting two-bubble layout shifts cause visible "jumping" during streaming.
 */
function hasStreamingAssistantTurn(turns: readonly Turn[]): boolean {
  for (let i = turns.length - 1; i >= 0; i--) {
    const turn = turns[i];
    if (turn.role !== 'assistant') continue;
    if (turn.status !== 'in_progress') return false;
    return (turn.content as readonly TurnContent[]).some(
      (block) => block.type === 'text' && (block as { isStreaming?: boolean }).isStreaming === true,
    );
  }
  return false;
}

export function useStreetBeatChatLegacy() {
  const v16 = useStreetBeatChat();
  const stateHook = useChatState();
  const flow = useConversationFlow();

  const rawMessages = stateHook.chatState.messages;
  const isConnectingToThread = stateHook.chatState.isConnectingToThread;

  const isStreaming = useMemo(
    () => hasStreamingAssistantTurn(v16.turns ?? []),
    [v16.turns],
  );

  const messages = useMemo(
    () => {
      // Find the trailing in-progress assistant message, if any. It's the LAST
      // message in chatState (assistants always trail the user that triggered
      // the run). When a stream is in flight we hide it from `messages` so the
      // chat panel renders only the dedicated `streamingMessage` bubble — see
      // hasStreamingAssistantTurn for the rationale.
      const lastIdx = rawMessages.length - 1;
      const tail = lastIdx >= 0 ? rawMessages[lastIdx] : undefined;
      const tailIsLiveAssistant =
        isStreaming && tail !== undefined && tail.role === 'assistant';

      return rawMessages.filter((m, idx) => {
        if (tailIsLiveAssistant && idx === lastIdx) return false;
        // Rule: a real user message never has a `name` set. Any message with
        // `role: 'user'` AND a non-empty `name` is back-channel content (agent
        // self-feedback, guardrail/validation messages, tool routing) that ACE
        // injects into the conversation as if it were from the user — but it's
        // never user-visible. Filter it out unconditionally.
        const named = (m as { name?: string | null }).name;
        if (m.role === 'user' && typeof named === 'string' && named.length > 0) return false;
        // Belt-and-suspenders for any other role: ACE may also flag internal
        // messages with `internal: true` (live stream — see ace_0069 migration).
        // The flag is dropped on history replay, which is why the rule above is
        // the primary filter; this catches the live-stream case for any role.
        if ((m as { internal?: boolean }).internal === true) return false;
        if (m.role !== 'assistant') return true;
        return isDisplayableAssistantMessage(m);
      });
    },
    [rawMessages, isStreaming],
  );

  const streamingMessage = useMemo(
    () => pickStreamingText(rawMessages, isStreaming),
    [rawMessages, isStreaming],
  );

  return {
    // pass-through from v16 hook
    threadId: v16.threadId,
    pendingAction: v16.pendingAction,
    cancel: v16.cancel,
    handleAction: v16.handleAction,

    // recovered via useChatState (chatState fields)
    messages,
    isConnectingToThread,

    // recovered via useConversationFlow (renamed methods)
    createThreadAndStart: flow.createThreadAndStart,
    continueThread: flow.continueThread,
    reconnectToThread: flow.reconnectToThread,

    // derived from turns
    streamingMessage,
  };
}
